export type AdvisoryReportingYearType = 'calendar' | 'fiscal';
export type AdvisoryReportingQuarter = 1 | 2 | 3 | 4;

export interface ReportingPeriodV01 {
  year: number;
  quarter: AdvisoryReportingQuarter | null;
  endQuarter: AdvisoryReportingQuarter | null;
  yearType: AdvisoryReportingYearType;
  fiscalStartMonth: number;
}

export type ReportingPeriodValidation =
  | { ok: true; value: ReportingPeriodV01 }
  | { ok: false; issue: 'invalid-reporting-period' };

const PERIOD_KEYS = ['year', 'quarter', 'endQuarter', 'yearType', 'fiscalStartMonth'] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const hasExactKeys = (value: Record<string, unknown>, keys: readonly string[]): boolean => {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
};

const isQuarter = (value: unknown): value is AdvisoryReportingQuarter =>
  value === 1 || value === 2 || value === 3 || value === 4;

/** Closed, non-throwing validator shared by SSE, Firestore, and the editor. */
export function validateReportingPeriodV01(input: unknown): ReportingPeriodValidation {
  if (!isRecord(input) || !hasExactKeys(input, PERIOD_KEYS)) {
    return { ok: false, issue: 'invalid-reporting-period' };
  }

  const { year, quarter, endQuarter, yearType, fiscalStartMonth } = input;
  if (
    !Number.isInteger(year) ||
    (year as number) < 1900 ||
    (year as number) > 9999 ||
    (quarter !== null && !isQuarter(quarter)) ||
    (endQuarter !== null && !isQuarter(endQuarter)) ||
    (yearType !== 'calendar' && yearType !== 'fiscal') ||
    !Number.isInteger(fiscalStartMonth) ||
    (fiscalStartMonth as number) < 1 ||
    (fiscalStartMonth as number) > 12
  ) {
    return { ok: false, issue: 'invalid-reporting-period' };
  }
  if (quarter === null && endQuarter !== null) {
    return { ok: false, issue: 'invalid-reporting-period' };
  }
  if (quarter !== null && endQuarter !== null && endQuarter < quarter) {
    return { ok: false, issue: 'invalid-reporting-period' };
  }
  if (yearType === 'calendar' && fiscalStartMonth !== 1) {
    return { ok: false, issue: 'invalid-reporting-period' };
  }

  return {
    ok: true,
    value: {
      year: year as number,
      quarter: quarter as AdvisoryReportingQuarter | null,
      endQuarter:
        quarter !== null && endQuarter === quarter
          ? null
          : (endQuarter as AdvisoryReportingQuarter | null),
      yearType,
      fiscalStartMonth: fiscalStartMonth as number,
    },
  };
}

export const defaultReportingPeriodV01 = (year = new Date().getFullYear()): ReportingPeriodV01 => ({
  year,
  quarter: null,
  endQuarter: null,
  yearType: 'calendar',
  fiscalStartMonth: 1,
});

export const reportingPeriodsEqual = (
  left: ReportingPeriodV01,
  right: ReportingPeriodV01
): boolean =>
  left.year === right.year &&
  left.quarter === right.quarter &&
  left.endQuarter === right.endQuarter &&
  left.yearType === right.yearType &&
  left.fiscalStartMonth === right.fiscalStartMonth;

export const reportingPeriodExactKeys = hasExactKeys;
